import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Loader2, Download, Megaphone } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const KEYWORD = 'neo';

const fmt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => '₺' + Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
const fmtPct = (n) => `%${Number(n || 0).toFixed(2)}`;

const DATE_PRESETS = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last7', label: 'Son 7 Gün' },
  { value: 'last14', label: 'Son 14 Gün' },
  { value: 'last30', label: 'Son 30 Gün' },
  { value: 'last90', label: 'Son 90 Gün' },
  { value: 'thisMonth', label: 'Bu Ay' },
  { value: 'lastMonth', label: 'Geçen Ay' },
  { value: 'custom', label: 'Özel' }
];

const SOCIAL_HOSTS = ['facebook', 'fb.', 'instagram', 'ig.', 'tiktok', 'twitter', 'x.com', 't.co', 'linkedin', 'pinterest', 'snapchat', 'youtube', 'youtu.be', 'reddit', 'meta'];
const SEARCH_HOSTS = ['google', 'bing', 'yahoo', 'yandex', 'duckduckgo', 'ecosia', 'baidu'];

function classifyChannel(source, medium) {
  const s = (source || '').toLowerCase().trim();
  const m = (medium || '').toLowerCase().trim();
  const isSocialHost = SOCIAL_HOSTS.some((h) => s.includes(h));
  const isSearchHost = SEARCH_HOSTS.some((h) => s.includes(h));

  if (s === '(direct)' || m === '(none)' || (m === '' && s === '(direct)')) return 'Direct';
  if (m === 'email' || s === 'email' || m === 'newsletter') return 'Email';
  if (m === 'sms') return 'SMS';
  if (m === 'push' || m === 'push_notification') return 'Push';
  if (m === 'affiliate' || m === 'affiliates') return 'Affiliate';

  if (m === 'cpc' || m === 'ppc' || m === 'paidsearch' || m === 'paid_search' || m.startsWith('paid')) {
    if (isSocialHost) return 'Paid Social';
    return 'Paid Search';
  }
  if (m === 'display' || m === 'cpm' || m === 'banner' || m.includes('display')) return 'Display';
  if (m === 'video' || m === 'youtube') return 'Video';

  if (m === 'organic') {
    if (isSocialHost) return 'Organic Social';
    return 'Organic Search';
  }

  if (m === 'referral' || m === 'social' || m === 'social-network' || m === 'social_network') {
    if (isSocialHost) return 'Organic Social';
    if (isSearchHost) return 'Organic Search';
    return 'Referral';
  }

  if (isSocialHost) return 'Organic Social';
  if (isSearchHost) return 'Organic Search';
  return 'Other';
}

const CHANNEL_COLORS = {
  'Paid Search': 'bg-blue-50 text-blue-700 border-blue-200',
  'Organic Search': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Paid Social': 'bg-violet-50 text-violet-700 border-violet-200',
  'Organic Social': 'bg-pink-50 text-pink-700 border-pink-200',
  'Direct': 'bg-gray-100 text-gray-700 border-gray-200',
  'Email': 'bg-amber-50 text-amber-700 border-amber-200',
  'Referral': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Display': 'bg-orange-50 text-orange-700 border-orange-200',
  'Video': 'bg-red-50 text-red-700 border-red-200',
  'Affiliate': 'bg-teal-50 text-teal-700 border-teal-200',
  'SMS': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Push': 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  'Other': 'bg-gray-50 text-gray-600 border-gray-200'
};

export default function PageSourceMediumLive() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId');

  const [dateRange, setDateRange] = useState('last7');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resp, setResp] = useState(null);

  // Campaign data (raw rows from /api/google-ads/unified-campaign-performance and /api/meta-ads/v2-campaign-performance)
  const [googleCampRows, setGoogleCampRows] = useState([]);
  const [metaCampRows, setMetaCampRows] = useState([]);
  const [campLoading, setCampLoading] = useState(false);
  const [campError, setCampError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setCampLoading(true);
    setError(null);
    setCampError(null);
    try {
      const params = new URLSearchParams();
      params.set('dateRange', dateRange);
      if (dateRange === 'custom') {
        if (!customStart || !customEnd) {
          setError('Lütfen başlangıç ve bitiş tarihi seçin.');
          setLoading(false);
          setCampLoading(false);
          return;
        }
        params.set('startDate', customStart);
        params.set('endDate', customEnd);
      }
      params.set('keywords', KEYWORD);

      // Always pull last90 for campaigns (frontend post-filters by GA4 resolved dates).
      // This avoids the Meta endpoint not supporting last14/custom and gives consistent windows.
      const campParams = new URLSearchParams();
      campParams.set('dateRange', 'last90');

      const ga4Url = `${API_BASE}/api/google-analytics/ga4/page-source-medium?${params.toString()}`;
      const googleUrl = `${API_BASE}/api/google-ads/unified-campaign-performance?${campParams.toString()}`;
      const metaUrl = `${API_BASE}/api/meta-ads/v2-campaign-performance?${campParams.toString()}`;
      const headers = { 'Content-Type': 'application/json', 'X-Brand-ID': brandId };

      const [ga4Res, gRes, mRes] = await Promise.allSettled([
        fetch(ga4Url, { credentials: 'include', headers }).then((r) => r.json().then((d) => ({ ok: r.ok, d }))),
        fetch(googleUrl, { credentials: 'include', headers }).then((r) => r.json().then((d) => ({ ok: r.ok, d }))),
        fetch(metaUrl, { credentials: 'include', headers }).then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      ]);

      // GA4
      if (ga4Res.status === 'fulfilled' && ga4Res.value.ok && !ga4Res.value.d.error) {
        setResp(ga4Res.value.d);
      } else {
        const msg = ga4Res.status === 'fulfilled'
          ? (ga4Res.value.d?.error || ga4Res.value.d?.details || 'GA4 isteği başarısız')
          : (ga4Res.reason?.message || 'GA4 isteği başarısız');
        setError(msg);
        setResp(null);
      }

      // Google Ads campaigns
      if (gRes.status === 'fulfilled' && gRes.value.ok && Array.isArray(gRes.value.d)) {
        setGoogleCampRows(gRes.value.d);
      } else {
        setGoogleCampRows([]);
        if (gRes.status === 'fulfilled' && gRes.value.d?.error) {
          setCampError((prev) => prev || `Google Ads: ${gRes.value.d.error}`);
        }
      }

      // Meta Ads campaigns
      if (mRes.status === 'fulfilled' && mRes.value.ok && Array.isArray(mRes.value.d)) {
        setMetaCampRows(mRes.value.d);
      } else {
        setMetaCampRows([]);
        if (mRes.status === 'fulfilled' && mRes.value.d?.error) {
          setCampError((prev) => prev || `Meta Ads: ${mRes.value.d.error}`);
        }
      }
    } catch (e) {
      console.error('PageSourceMediumLive fetch error:', e);
      setError(e.message || 'Veri alınamadı');
      setResp(null);
    } finally {
      setLoading(false);
      setCampLoading(false);
    }
  }, [brandId, dateRange, customStart, customEnd]);

  useEffect(() => {
    if (brandId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Auto-refetch on date preset change (skip custom - wait for both dates + button)
  useEffect(() => {
    if (!brandId) return;
    if (dateRange === 'custom') return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const rows = resp?.rows || [];
  const totals = resp?.totals || { sessions: 0, users: 0, engagedSessions: 0, conversions: 0, revenue: 0 };

  // Date window for campaign post-filtering (use GA4 resolved dates)
  const windowStart = resp?.startDate || null;
  const windowEnd = resp?.endDate || null;

  const inWindow = useCallback(
    (dateStr) => {
      if (!windowStart || !windowEnd || !dateStr) return false;
      const d = String(dateStr).slice(0, 10);
      return d >= windowStart && d <= windowEnd;
    },
    [windowStart, windowEnd]
  );

  // Aggregate Google Ads campaigns whose name contains "neo"
  const googleCampaignAgg = useMemo(() => {
    const map = new Map();
    googleCampRows.forEach((r) => {
      const name = r.campaign_name || '';
      if (!name.toLowerCase().includes(KEYWORD)) return;
      if (!inWindow(r.date)) return;
      const cost = Number(r.google_cost || 0);
      const clicks = Number(r.google_clicks || 0);
      const impressions = Number(r.google_impressions || 0);
      const conversions = Number(r.google_conversions || 0);
      const convValue = Number(r.google_conversion_value || 0);
      // Skip rows where Google didn't actually serve this campaign
      if (cost === 0 && clicks === 0 && impressions === 0 && conversions === 0) return;

      const cur = map.get(name) || {
        campaign: name,
        cost: 0,
        clicks: 0,
        impressions: 0,
        conversions: 0,
        revenue: 0
      };
      cur.cost += cost;
      cur.clicks += clicks;
      cur.impressions += impressions;
      cur.conversions += conversions;
      cur.revenue += convValue;
      map.set(name, cur);
    });
    return [...map.values()].sort((a, b) => b.cost - a.cost);
  }, [googleCampRows, inWindow]);

  // Aggregate Meta Ads campaigns whose name contains "neo"
  const metaCampaignAgg = useMemo(() => {
    const map = new Map();
    metaCampRows.forEach((r) => {
      const name = r.campaignName || '';
      if (!name.toLowerCase().includes(KEYWORD)) return;
      if (!inWindow(r.date)) return;
      const cur = map.get(name) || {
        campaign: name,
        cost: 0,
        clicks: 0,
        impressions: 0,
        conversions: 0,
        revenue: 0
      };
      cur.cost += Number(r.spend || 0);
      cur.clicks += Number(r.clicks || 0);
      cur.impressions += Number(r.impressions || 0);
      cur.conversions += Number(r.purchaseConversions || 0);
      cur.revenue += Number(r.purchaseValue || 0);
      map.set(name, cur);
    });
    return [...map.values()].sort((a, b) => b.cost - a.cost);
  }, [metaCampRows, inWindow]);

  const sumCampaigns = (arr) =>
    arr.reduce(
      (acc, r) => {
        acc.cost += r.cost;
        acc.clicks += r.clicks;
        acc.impressions += r.impressions;
        acc.conversions += r.conversions;
        acc.revenue += r.revenue;
        return acc;
      },
      { cost: 0, clicks: 0, impressions: 0, conversions: 0, revenue: 0 }
    );

  const googleTotals = useMemo(() => sumCampaigns(googleCampaignAgg), [googleCampaignAgg]);
  const metaTotals = useMemo(() => sumCampaigns(metaCampaignAgg), [metaCampaignAgg]);

  // Aggregate all neo rows by channel
  const channelRows = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const ch = classifyChannel(r.source, r.medium);
      const cur = map.get(ch) || {
        channel: ch,
        sessions: 0,
        users: 0,
        engagedSessions: 0,
        conversions: 0,
        revenue: 0
      };
      cur.sessions += r.sessions;
      cur.users += r.users;
      cur.engagedSessions += r.engagedSessions;
      cur.conversions += r.conversions;
      cur.revenue += r.revenue;
      map.set(ch, cur);
    });
    return [...map.values()].sort((a, b) => b.sessions - a.sessions);
  }, [rows]);

  const exportCsv = () => {
    if (!channelRows.length) return;
    const header = ['Channel', 'Sessions', 'Users', 'Engaged Sessions', 'Share %'];
    const lines = [header.join(',')];
    channelRows.forEach((r) => {
      const share = totals.sessions > 0 ? (r.sessions / totals.sessions) * 100 : 0;
      lines.push([
        `"${r.channel}"`,
        r.sessions,
        r.users,
        r.engagedSessions,
        share.toFixed(2)
      ].join(','));
    });
    lines.push(['"TOPLAM"', totals.sessions, totals.users, totals.engagedSessions, '100.00'].join(','));
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `neo-channels-${resp?.startDate || ''}-${resp?.endDate || ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-4">
      {/* Compact header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Neo Sayfaları → Channel</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            URL'sinde <span className="font-mono bg-gray-100 px-1 rounded">neo</span> geçen sayfaların kanal kırılımı (canlı GA4)
            {resp && (
              <>
                {' · '}
                <strong>{resp.startDate}</strong> → <strong>{resp.endDate}</strong>
                {' · '}
                <span className="text-gray-400">{fmt(resp.rowCount)} ham satır birleştirildi</span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
          >
            {DATE_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          {dateRange === 'custom' && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <button
                onClick={fetchData}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Getir
              </button>
            </>
          )}
          <button
            onClick={exportCsv}
            disabled={!channelRows.length}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Compact totals strip */}
      {resp && !loading && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl px-5 py-3 flex flex-wrap items-center gap-x-8 gap-y-1 text-sm">
          <Stat label="Sessions" value={fmt(totals.sessions)} />
          <Stat label="Users" value={fmt(totals.users)} />
          <Stat label="Engaged" value={fmt(totals.engagedSessions)} />
        </div>
      )}

      {/* Channel table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2.5 px-4 font-semibold text-gray-600">Channel</th>
                <th className="text-right py-2.5 px-3 font-semibold text-gray-600">Sessions</th>
                <th className="text-right py-2.5 px-3 font-semibold text-gray-600">Users</th>
                <th className="text-right py-2.5 px-3 font-semibold text-gray-600">Engaged</th>
                <th className="text-right py-2.5 px-3 font-semibold text-gray-600 w-32">Share</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <Loader2 className="w-7 h-7 text-blue-600 animate-spin inline" />
                  </td>
                </tr>
              )}
              {!loading &&
                channelRows.map((row) => {
                  const share = totals.sessions > 0 ? (row.sessions / totals.sessions) * 100 : 0;
                  const colorCls = CHANNEL_COLORS[row.channel] || CHANNEL_COLORS.Other;
                  return (
                    <tr key={row.channel} className="border-b border-gray-100 hover:bg-blue-50/30 transition-colors">
                      <td className="py-2 px-4">
                        <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium border ${colorCls}`}>
                          {row.channel}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono font-medium">{fmt(row.sessions)}</td>
                      <td className="py-2 px-3 text-right font-mono text-gray-600">{fmt(row.users)}</td>
                      <td className="py-2 px-3 text-right font-mono text-gray-600">{fmt(row.engagedSessions)}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${Math.min(100, share)}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs text-gray-600 w-12 text-right">{fmtPct(share)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {!loading && channelRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-gray-400 text-sm">
                    Veri bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
            {!loading && channelRows.length > 0 && (
              <tfoot>
                <tr className="bg-gray-900 text-white font-semibold">
                  <td className="py-2.5 px-4">TOPLAM</td>
                  <td className="py-2.5 px-3 text-right font-mono">{fmt(totals.sessions)}</td>
                  <td className="py-2.5 px-3 text-right font-mono">{fmt(totals.users)}</td>
                  <td className="py-2.5 px-3 text-right font-mono">{fmt(totals.engagedSessions)}</td>
                  <td className="py-2.5 px-3 text-right font-mono">100%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Neo campaigns section */}
      <div className="pt-4 space-y-4">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-gray-500" />
          <h2 className="text-base font-semibold text-gray-800">Neo Kampanyaları</h2>
          <span className="text-xs text-gray-400">— ismi "neo" geçen kampanyalar</span>
        </div>

        {campError && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-2 text-xs">
            {campError}
          </div>
        )}

        <CampaignTable
          title="Google Ads"
          accent="bg-blue-600"
          rows={googleCampaignAgg}
          totals={googleTotals}
          loading={campLoading}
        />

        <CampaignTable
          title="Meta Ads"
          accent="bg-violet-600"
          rows={metaCampaignAgg}
          totals={metaTotals}
          loading={campLoading}
        />
      </div>
    </div>
  );
}

function CampaignTable({ title, accent, rows, totals, loading }) {
  const roas = (rev, cost) => (cost > 0 ? rev / cost : 0);
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50">
        <span className={`inline-block w-2 h-2 rounded-full ${accent}`} />
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <span className="text-xs text-gray-400">({rows.length} kampanya)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left py-2 px-4 font-semibold text-gray-600">Kampanya</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-600">Spend</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-600">Impr.</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-600">Clicks</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-600">CTR</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-600">Conv.</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-600">Revenue</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-600">ROAS</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="py-8 text-center">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin inline" />
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => {
                const ctr = r.impressions > 0 ? (r.clicks / r.impressions) * 100 : 0;
                const ro = roas(r.revenue, r.cost);
                return (
                  <tr key={r.campaign} className="border-b border-gray-100 hover:bg-gray-50/60">
                    <td className="py-2 px-4 text-xs text-gray-800 max-w-[360px] truncate" title={r.campaign}>
                      {r.campaign}
                    </td>
                    <td className="py-2 px-3 text-right font-mono font-medium">{fmtMoney(r.cost)}</td>
                    <td className="py-2 px-3 text-right font-mono text-gray-600">{fmt(r.impressions)}</td>
                    <td className="py-2 px-3 text-right font-mono text-gray-600">{fmt(r.clicks)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs text-gray-500">{fmtPct(ctr)}</td>
                    <td className="py-2 px-3 text-right font-mono text-emerald-700">
                      {r.conversions > 0 ? Number(r.conversions).toLocaleString('tr-TR', { maximumFractionDigits: 1 }) : '-'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono font-semibold text-green-700">
                      {r.revenue > 0 ? fmtMoney(r.revenue) : '-'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {ro > 0 ? (
                        <span className={ro >= 1 ? 'text-emerald-600 font-semibold' : 'text-red-500'}>
                          {ro.toFixed(2)}x
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="py-8 text-center text-gray-400 text-xs">
                  Bu tarih aralığında "neo" geçen {title} kampanyası bulunamadı.
                </td>
              </tr>
            )}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot>
              <tr className="bg-gray-900 text-white font-semibold text-xs">
                <td className="py-2 px-4">TOPLAM</td>
                <td className="py-2 px-3 text-right font-mono">{fmtMoney(totals.cost)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmt(totals.impressions)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmt(totals.clicks)}</td>
                <td className="py-2 px-3 text-right font-mono">
                  {totals.impressions > 0 ? fmtPct((totals.clicks / totals.impressions) * 100) : '-'}
                </td>
                <td className="py-2 px-3 text-right font-mono">
                  {totals.conversions > 0
                    ? Number(totals.conversions).toLocaleString('tr-TR', { maximumFractionDigits: 1 })
                    : '-'}
                </td>
                <td className="py-2 px-3 text-right font-mono">{fmtMoney(totals.revenue)}</td>
                <td className="py-2 px-3 text-right font-mono">
                  {totals.cost > 0 ? `${(totals.revenue / totals.cost).toFixed(2)}x` : '-'}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-blue-100">{label}</span>
      <span className="text-lg font-bold leading-tight">{value}</span>
    </div>
  );
}
